/**
 * Auth event system for handling authentication events across API instances
 * Used to notify components when 401 Unauthorized errors occur
 * Tracks failed requests and provides functionality to retry them after login
 */
import axios, { AxiosRequestConfig } from 'axios';
import type { Operation } from '@apollo/client';
import { getAuthToken, removeAuthToken } from './auth-token';

/**
 * Extended AxiosRequestConfig to include GraphQL operation data
 */
interface ExtendedRequestConfig extends AxiosRequestConfig {
  // Flag to track retry attempts
  _retry?: boolean;
  // Original GraphQL operation for Apollo Client requests
  _graphQLOperation?: Operation;
}

/**
 * Interface for a failed request that can be retried
 */
interface FailedRequest {
  config: ExtendedRequestConfig;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

/**
 * Type definition for auth event listeners
 */
export type AuthEventListener = () => void;

/**
 * Store failed requests that resulted in 401 errors
 */
let failedQueue: FailedRequest[] = [];

/**
 * Global array of auth error event listeners
 */
let authErrorListeners: AuthEventListener[] = [];

/**
 * Subscribe to auth error events (401 unauthorized)
 * @param listener - Callback function to be called when auth error occurs
 * @returns Function to unsubscribe the listener
 */
export const onAuthError = (listener: AuthEventListener): (() => void) => {
  authErrorListeners.push(listener);
  return () => {
    authErrorListeners = authErrorListeners.filter(l => l !== listener);
  };
};

/**
 * Add a failed request to the queue
 * @param config - The original request configuration, may include GraphQL operation data
 * @returns A promise that will be resolved when the request is retried
 */
export const addToFailedQueue = (config: ExtendedRequestConfig): Promise<any> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({
      config,
      resolve,
      reject
    });
    
    // If this is the first failed request, emit auth error
    if (failedQueue.length === 1) {
      authErrorListeners.forEach(listener => listener());
    }
  });
};

/**
 * Trigger auth error event to notify all listeners
 * Called when a 401 Unauthorized response is received
 */
export const emitAuthError = (): void => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Auth error detected: notifying listeners');
  }
  authErrorListeners.forEach(listener => listener());
};

/**
 * Clear user authentication data from storage
 * Used when 401 Unauthorized responses are received
 */
export const clearAuthData = (): void => {
  removeAuthToken();
};

/**
 * Process failed requests after successful login
 * Retries all requests that failed due to 401 errors
 * Supports both REST API requests and GraphQL operations
 */
export const retryFailedRequests = (): void => {
  // Don't do anything if the queue is empty
  if (failedQueue.length === 0) {
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log(`Retrying ${failedQueue.length} failed requests after authentication`);
  }

  // Process all failed requests
  failedQueue.forEach(request => {
    // Check if this is a GraphQL operation
    if (request.config._graphQLOperation) {
      // We already have the promise chain set up in the errorLink
      // Just need to resolve to continue the Apollo operation
      request.resolve();
      return;
    }

    // For regular REST API requests, use Axios to retry
    const token = getAuthToken();
    const retryAxios = axios.create();

    const config = {
      ...request.config,
      headers: {
        ...request.config.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    // Retry the request
    retryAxios(config)
      .then(response => {
        request.resolve(response);
      })
      .catch(error => {
        request.reject(error);
      });
  });

  // Clear the queue
  failedQueue = [];
};

/**
 * Clear the failed requests queue without retrying
 * Used when the user chooses not to log in or closes the modal
 */
export const clearFailedRequests = (): void => {
  if (failedQueue.length === 0) {
    return;
  }
  
  if (process.env.NODE_ENV !== 'production') {
    console.log(`Clearing ${failedQueue.length} failed requests without retrying`);
  }
  
  failedQueue.forEach(request => {
    request.reject(new Error('Authentication failed or canceled'));
  });
  failedQueue = [];
};
